import { useState } from 'react'
import {
  Card, Table, Button, Space, Typography, Tag, Row, Col,
  Select, Modal, Form, Input, Drawer, DatePicker, Tabs,
  message,
} from 'antd'
import { BankOutlined, PlusOutlined, UploadOutlined, DeleteOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title, Text } = Typography
const API = ''

function fmtINR(v: number) {
  return `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
}

const BRS_STATUS_COLORS: Record<string, string> = {
  UNMATCHED: 'error', MATCHED: 'success', IN_TRANSIT: 'processing', REVERSED: 'default',
}

export default function BankReconciliationPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const qc = useQueryClient()

  const [selectedAccount, setSelectedAccount] = useState<string | undefined>()
  const [statusFilter, setStatusFilter] = useState<string>('ALL')
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null)

  const [matchModal, setMatchModal] = // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useState<any>(null)
  const [matchForm] = Form.useForm()
  const [uploadDrawer, setUploadDrawer] = useState(false)
  const [uploadForm] = Form.useForm()

  const { data: accountsData } = useQuery({
    queryKey: ['bank-accounts', plantId],
    queryFn: () =>
      apiClient.get(`${API}/accounting/bank-accounts`, { params: { plant_id: plantId } }).then(r => r.data),
    enabled: !!plantId,
  })

  const { data: accountDetail } = useQuery({
    queryKey: ['bank-account-detail', selectedAccount],
    queryFn: () =>
      apiClient.get(`${API}/accounting/bank-accounts/${selectedAccount}`).then(r => r.data),
    enabled: !!selectedAccount,
  })

  const { data: brsData, isLoading } = useQuery({
    queryKey: ['brs-transactions', selectedAccount, statusFilter, dateRange],
    queryFn: () =>
      apiClient.get(`${API}/accounting/brs`, {
        params: {
          bank_account_id: selectedAccount,
          status: statusFilter !== 'ALL' ? statusFilter : undefined,
          from_date: dateRange?.[0]?.format('YYYY-MM-DD') || undefined,
          to_date: dateRange?.[1]?.format('YYYY-MM-DD') || undefined,
          limit: 500,
        },
      }).then(r => r.data),
    enabled: !!selectedAccount,
  })

  const { data: brsStats } = useQuery({
    queryKey: ['brs-stats', selectedAccount, dateRange],
    queryFn: () =>
      apiClient.get(`${API}/accounting/brs/summary`, {
        params: {
          bank_account_id: selectedAccount,
          from_date: dateRange?.[0]?.format('YYYY-MM-DD') || undefined,
          to_date: dateRange?.[1]?.format('YYYY-MM-DD') || undefined,
        },
      }).then(r => r.data),
    enabled: !!selectedAccount,
  })

  const matchMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiClient.post(`${API}/accounting/brs/${id}/match`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['brs-transactions'] })
      qc.invalidateQueries({ queryKey: ['brs-stats'] })
      message.success('Transaction matched')
      setMatchModal(null)
      matchForm.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const uploadMut = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiClient.post(`${API}/accounting/brs/upload-statement`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['brs-transactions'] })
      message.success('Statement uploaded')
      setUploadDrawer(false)
      uploadForm.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const accounts = Array.isArray(accountsData) ? accountsData : []
  const transactions = Array.isArray(brsData?.entries ?? brsData) ? (brsData?.entries ?? brsData) : []
  const summary = brsStats ?? {}

  const diff = Number(summary.bank_closing ?? 0) - Number(summary.book_balance ?? 0)

  const columns = [
    { title: 'Date', dataIndex: 'transaction_date', key: 'transaction_date', width: 100,
      render: (v: string) => v ? dayjs(v).format('DD-MMM-YY') : '—' },
    { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true,
      render: (v: string) => <Text ellipsis={{ tooltip: v }}>{v ?? '—'}</Text> },
    { title: 'Amount', key: 'amount', width: 130, align: 'right' as const,
      render: (_: unknown, r: any) => (
        <span style={{ color: r.dr_cr === 'DR' ? '#ff4d4f' : '#52c41a', fontWeight: 600 }}>
          {r.dr_cr === 'DR' ? '−' : '+'}{fmtINR(r.amount)}
        </span>
      ) },
    { title: 'Reference', dataIndex: 'reference_no', key: 'reference_no', width: 130 },
    { title: 'Status', dataIndex: 'brs_status', key: 'brs_status', width: 110,
      render: (v: string) => <Tag color={BRS_STATUS_COLORS[v] ?? 'default'}>{v?.replace('_', ' ')}</Tag> },
    { title: 'Matched Voucher', dataIndex: 'matched_voucher', key: 'matched_voucher', width: 140,
      render: (v: string) => v ? <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{v}</span> : '—' },
    { title: 'Action', key: 'action', width: 80,
      render: (_: unknown, r: any) => r.brs_status === 'UNMATCHED'
        ? <Button size="small" type="primary" onClick={() => setMatchModal(r)}>Match</Button>
        : null },
  ]

  const statusTabs = [
    { key: 'ALL', label: 'All' },
    { key: 'UNMATCHED', label: 'Unmatched' },
    { key: 'MATCHED', label: 'Matched' },
    { key: 'IN_TRANSIT', label: 'In Transit' },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <BankOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Bank Reconciliation (BRS)
      </Title>
      <Text type="secondary">Match bank statement transactions with accounting vouchers</Text>

      <Row gutter={16} style={{ marginTop: 16 }}>
        {/* Account Selector */}
        <Col span={6}>
          <Card size="small" title="Bank Account">
            <Select
              value={plantId}
              onChange={setPlantId}
              style={{ width: '100%', marginBottom: 8 }}
              placeholder="Select Plant"
              options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))}
            />
            <Select
              value={selectedAccount}
              onChange={setSelectedAccount}
              style={{ width: '100%' }}
              placeholder="Select Bank Account"
              options={accounts.map((a: any) => ({ value: a.id, label: `${a.bank_name} — ${a.account_no}` }))}
            />
            {accountDetail && (
              <div style={{ marginTop: 12 }}>
                <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>{accountDetail.bank_name}</Text>
                <Text strong style={{ fontFamily: 'monospace' }}>{accountDetail.account_no}</Text>
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Book Balance: </Text>
                  <Text strong style={{ color: '#1677ff' }}>{fmtINR(accountDetail.book_balance ?? 0)}</Text>
                </div>
              </div>
            )}
          </Card>
        </Col>

        {/* BRS Table */}
        <Col span={18}>
          <Card
            size="small"
            extra={
              <Space>
                <DatePicker.RangePicker onChange={v => setDateRange(v as any)} style={{ width: 220 }} />
                <Button icon={<UploadOutlined />} onClick={() => setUploadDrawer(true)}>Upload Statement</Button>
              </Space>
            }
          >
            <Tabs
              activeKey={statusFilter}
              onChange={setStatusFilter}
              items={statusTabs}
              size="small"
              style={{ marginBottom: 12 }}
            />
            <Table
              dataSource={transactions}
              columns={columns}
              rowKey="id"
              loading={isLoading}
              size="small"
              pagination={{ pageSize: 50 }}
              scroll={{ x: 900 }}
            />

            {/* Summary Box */}
            {selectedAccount && (
              <Card
                size="small"
                style={{ marginTop: 16, background: diff !== 0 ? '#fff2e8' : '#f6ffed' }}
              >
                <Row gutter={16}>
                  <Col span={6}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Opening Balance</Text>
                    <div><Text strong>{fmtINR(summary.opening_balance ?? 0)}</Text></div>
                  </Col>
                  <Col span={4}>
                    <Text type="secondary" style={{ fontSize: 12 }}>+ Credits</Text>
                    <div><Text strong style={{ color: '#52c41a' }}>{fmtINR(summary.total_credits ?? 0)}</Text></div>
                  </Col>
                  <Col span={4}>
                    <Text type="secondary" style={{ fontSize: 12 }}>− Debits</Text>
                    <div><Text strong style={{ color: '#ff4d4f' }}>{fmtINR(summary.total_debits ?? 0)}</Text></div>
                  </Col>
                  <Col span={4}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Bank Closing</Text>
                    <div><Text strong>{fmtINR(summary.bank_closing ?? 0)}</Text></div>
                  </Col>
                  <Col span={3}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Book Balance</Text>
                    <div><Text strong>{fmtINR(summary.book_balance ?? 0)}</Text></div>
                  </Col>
                  <Col span={3}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Difference</Text>
                    <div>
                      <Text strong style={{ color: Math.abs(diff) > 0.5 ? 'red' : '#52c41a' }}>
                        {fmtINR(diff)}
                      </Text>
                    </div>
                  </Col>
                </Row>
              </Card>
            )}
          </Card>
        </Col>
      </Row>

      {/* Match Modal */}
      <Modal
        title="Match Transaction"
        open={!!matchModal}
        onCancel={() => { setMatchModal(null); matchForm.resetFields() }}
        onOk={() =>
          matchForm.validateFields().then(vals =>
            matchMut.mutate({ id: matchModal.id, data: vals })
          )
        }
        confirmLoading={matchMut.isPending}
      >
        {matchModal && (
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary">Transaction: </Text>
            <Text>{matchModal.description} — {fmtINR(matchModal.amount)}</Text>
          </div>
        )}
        <Form form={matchForm} layout="vertical">
          <Form.Item name="voucher_no" label="Voucher Number" rules={[{ required: true }]}>
            <Input placeholder="e.g. PV-2024-0001" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Upload Statement Drawer */}
      <Drawer
        title="Upload Bank Statement"
        open={uploadDrawer}
        onClose={() => { setUploadDrawer(false); uploadForm.resetFields() }}
        width={600}
        footer={
          <Space style={{ float: 'right' }}>
            <Button onClick={() => { setUploadDrawer(false); uploadForm.resetFields() }}>Cancel</Button>
            <Button
              type="primary"
              loading={uploadMut.isPending}
              onClick={() =>
                uploadForm.validateFields().then(vals =>
                  uploadMut.mutate({
                    bank_account_id: selectedAccount,
                    plant_id: plantId,
                    rows: vals.rows?.map((r: any) => ({
                      ...r,
                      transaction_date: r.transaction_date?.format('YYYY-MM-DD'),
                    })) ?? [],
                  })
                )
              }
            >
              Upload
            </Button>
          </Space>
        }
      >
        <Form form={uploadForm} layout="vertical">
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            Add bank statement rows manually or paste from CSV:
          </Text>
          <Form.List name="rows">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }) => (
                  <Card key={key} size="small" style={{ marginBottom: 8, background: '#fafafa' }}>
                    <Row gutter={8} align="middle">
                      <Col span={5}>
                        <Form.Item {...rest} name={[name, 'transaction_date']} label="Date" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                          <DatePicker style={{ width: '100%' }} size="small" />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item {...rest} name={[name, 'description']} label="Description" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                          <Input size="small" />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item {...rest} name={[name, 'amount']} label="Amount" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                          <Input size="small" />
                        </Form.Item>
                      </Col>
                      <Col span={3}>
                        <Form.Item {...rest} name={[name, 'dr_cr']} label="Dr/Cr" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                          <Select size="small" options={[{ value: 'DR', label: 'DR' }, { value: 'CR', label: 'CR' }]} />
                        </Form.Item>
                      </Col>
                      <Col span={3}>
                        <Form.Item {...rest} name={[name, 'reference_no']} label="Ref" style={{ marginBottom: 0 }}>
                          <Input size="small" />
                        </Form.Item>
                      </Col>
                      <Col span={1}>
                        <Button danger size="small" icon={<DeleteOutlined />} onClick={() => remove(name)} style={{ marginTop: 22 }} />
                      </Col>
                    </Row>
                  </Card>
                ))}
                <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add()}>Add Row</Button>
              </>
            )}
          </Form.List>
        </Form>
      </Drawer>
    </div>
  )
}
